import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import type { ConfiguredField, PaletteField, SavedField } from '../../api/settingsEaseedeskFormBuilder'
import {
  createUpdateForm,
  removeUpdateForm,
  saveUpdateForm,
  updateFormEditorQuery,
  updateFormListQuery,
  updateFormsShellQuery,
  updatePaletteField,
} from '../../api/settingsEaseedeskUpdateForms'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import { FieldRow, Palette } from './TicketFormBuilderPage'

/**
 * Settings → Easeedesk → Update form builder.
 *
 * Element cu element fata de `version-3/easeedesk_settings/tickets/
 * update_form_builder/index.php` + `form_list.php` + `edit_form.php` +
 * `sortable_field.php`.
 *
 * Randul de camp si paleta sunt CHIAR CELE de la formularele de tichet
 * (`TicketFormBuilderPage`): machetele erau doua fisiere aproape identice, iar
 * ce le deosebea - coloana `visibility` in loc de `field_visibility`, si numai
 * campurile standard cu `update_form = 1` - sta in backend, unde e si tabela.
 * Doua desene identice tinute in doua locuri se despart la prima schimbare.
 */
function UpdateFormEditorPanel({
  form,
  onClose,
  onSaved,
}: {
  form: number
  onClose(): void
  onSaved(): void
}) {
  const editor = useQuery(updateFormEditorQuery(form))
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [fields, setFields] = useState<ConfiguredField[]>([])
  const [dragged, setDragged] = useState<number | null>(null)
  const [openPanel, setOpenPanel] = useState<'standard' | 'custom'>('standard')
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!editor.data) return
    setName(editor.data.formDetails.form_name)
    setDescription(editor.data.formDetails.form_description)
    setFields(editor.data.configured_fields)
  }, [editor.data])

  const chosen = new Set(fields.map((f) => f.key))

  const save = useMutation({
    mutationFn: () =>
      saveUpdateForm(
        form,
        name.trim(),
        description.trim(),
        fields.map<SavedField>((f) => ({
          ID: f.ID,
          custom_field: f.custom_field,
          required: f.required,
          visibility: f.visibility,
        })),
      ),
    onSuccess: () => {
      toast('Update form saved.', 'Success')
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  const add = useMutation({
    mutationFn: (field: PaletteField) => updatePaletteField(field.ID, field.custom_field ? 'custom' : 'standard'),
    onSuccess: (answer) => {
      const field = answer.field
      setFields((current) =>
        current.some((f) => f.key === field.key)
          ? current
          : [
              ...current,
              {
                config_id: 0,
                ID: field.ID,
                key: field.key,
                custom_field: field.custom_field,
                field_name: field.field_name,
                field_label: field.field_label,
                field_type: field.field_type,
                field_unique_id: field.field_unique_id,
                required: false,
                always: field.always,
                visibility: 0,
                order: current.length,
                options: [],
                default_values: [],
              },
            ],
      )
    },
    onError: () => toast('Could not add the field.', 'Error', 'error'),
  })

  if (editor.error) return <LoadFailed error={editor.error} onRetry={() => void editor.refetch()} what="This update form" />
  if (editor.isPending) return <Spinner />
  if (!editor.data) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> This form could not be opened.
      </div>
    )
  }

  const move = (index: number, delta: number) => {
    const target = index + delta
    if (target < 0 || target >= fields.length) return
    const next = [...fields]
    const [moved] = next.splice(index, 1)
    next.splice(target, 0, moved)
    setFields(next)
  }

  return (
    <div className="row" id="edit-created-form">
      <div className="col-md-7">
        <div style={{ marginBottom: 20 }}>
          <button className="btn btn-primary" type="button" disabled={save.isPending} onClick={() => save.mutate()}>
            <i className="fa fa-check" aria-hidden="true"></i> Save
          </button>{' '}
          <button className="btn btn-default" type="button" onClick={onClose}>
            <i className="fas fa-times" aria-hidden="true"></i> Cancel
          </button>
        </div>

        <div style={{ marginBottom: 20 }}>
          <input
            type="text"
            className="form-control"
            name="form-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <textarea
            className="form-control"
            name="form-description"
            style={{ maxWidth: '100%', marginTop: 8 }}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <ul className="sortable" style={{ border: '1px solid #ddd', padding: 20, backgroundColor: '#f5f5f5' }}>
          {fields.map((field, index) => (
            <FieldRow
              key={field.key}
              field={field}
              first={index === 0}
              last={index === fields.length - 1}
              onChange={(next) => setFields(fields.map((f) => (f.key === next.key ? next : f)))}
              onRemove={() => setFields(fields.filter((f) => f.key !== field.key))}
              onMove={(delta) => move(index, delta)}
              onDragStart={() => setDragged(index)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragged === null || dragged === index) return
                move(dragged, index - dragged)
                setDragged(null)
              }}
            />
          ))}
          {fields.length === 0 ? (
            <li className="color-light-grey" style={{ fontSize: 12 }}>
              No field on this update form yet. Tick one on the right.
            </li>
          ) : null}
        </ul>
      </div>

      <div className="col-md-5">
        <div className="panel-group" style={{ margin: 0 }}>
          <Palette
            title="Fields"
            fields={editor.data.fields}
            chosen={chosen}
            open={openPanel === 'standard'}
            onToggleOpen={() => setOpenPanel('standard')}
            onToggle={(field, on) =>
              on ? add.mutate(field) : setFields((current) => current.filter((f) => f.key !== field.key))
            }
            labelClass="label-info"
          />
          <Palette
            title="Custom fields"
            fields={editor.data.custom_fields}
            chosen={chosen}
            open={openPanel === 'custom'}
            onToggleOpen={() => setOpenPanel('custom')}
            onToggle={(field, on) =>
              on ? add.mutate(field) : setFields((current) => current.filter((f) => f.key !== field.key))
            }
            search={search}
            onSearch={setSearch}
            labelClass="label-primary"
          />
        </div>
      </div>
    </div>
  )
}

/** `newFormModal` din macheta constructorului de formulare de actualizare. */
function NewUpdateFormModal({ open, onClose, onSaved }: { open: boolean; onClose(): void; onSaved(): void }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  useEffect(() => {
    if (!open) return
    setName('')
    setDescription('')
  }, [open])

  const create = useMutation({
    mutationFn: () => createUpdateForm(name.trim(), description.trim()),
    onSuccess: () => {
      toast('New update form created.', 'Success')
      onClose()
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal id="newFormModal" labelledBy="newFormModalLabel" open={open} onClose={onClose} title="Create form">
      <form
        id="new-form"
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim() === '') return
          create.mutate()
        }}
      >
        <div className="form-group">
          <label htmlFor="update-form-name" className="control-label">
            Form name
          </label>
          <input
            type="text"
            className="form-control input-sm"
            id="update-form-name"
            name="form_name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="update-form-description" className="control-label">
            Description
          </label>
          <textarea
            id="update-form-description"
            className="form-control input-sm"
            name="form_description"
            style={{ maxWidth: '100%' }}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className="btn btn-primary submit-new-form" disabled={create.isPending}>
            Create
          </button>
        </div>
      </form>
    </Modal>
  )
}

/** `settings/easeedesk/update_form_builder`. */
export function UpdateFormBuilderPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(updateFormsShellQuery())
  const list = useQuery(updateFormListQuery())
  const [creating, setCreating] = useState(false)
  const [edited, setEdited] = useState(0)

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'update_form_builder'] })
  }

  const confirmRemove = useMutation({
    mutationFn: (form: number) => removeUpdateForm(form, true),
    onSuccess: (_answer, form) => {
      toast('Update form removed.', 'Success')
      if (edited === form) setEdited(0)
      reload()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  const remove = useMutation({
    mutationFn: (form: number) => removeUpdateForm(form, false),
    onSuccess: (answer, form) => {
      if (answer.confirm) {
        const used = (answer.used_by ?? []).join(', ')
        if (!window.confirm(`This update form is used by: ${used}.\nRemove it anyway?`)) return
        confirmRemove.mutate(form)
        return
      }
      toast('Update form removed.', 'Success')
      if (edited === form) setEdited(0)
      reload()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) return <LoadFailed error={shell.error} onRetry={() => void shell.refetch()} what="The update form builder" />
  if (!shell.data) return <Spinner />

  const forms = list.data?.form_list ?? []

  return (
    <div>
      <h4>
        <strong>Form</strong> builder
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage update <strong>forms</strong>
      </label>
      <button type="button" className="btn btn-success pull-right" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create new form
      </button>
      <hr />

      <div className="row" id="ticket-form-list">
        {list.error ? (
          <LoadFailed error={list.error} onRetry={() => void list.refetch()} what="The update form list" />
        ) : !list.data ? (
          <Spinner />
        ) : forms.length === 0 ? (
          <div className="col-md-12">
            <div className="well flip-bg-white" style={{ backgroundColor: '#D4EEFF' }}>
              {/* `od-note-title`, ca la Comment types: `body.od-console h4:not([class]) *`
                  pune fontul de titlu si pe `<i>`, si iconita iesea casuta goala.
                  O clasa, oricare, scoate `h4` de sub regula. */}
              <h4 className="od-note-title">
                <i className="fas fa-exclamation-triangle"></i> <strong>INFO</strong>
              </h4>
              <p>
                Form list is empty. Click <strong>+ Create new form</strong> button.
              </p>
              <div className="flip"></div>
            </div>
          </div>
        ) : (
          <div className="col-md-12">
            <table className="table table-hover forms-list">
              <tbody>
                {forms.map((form) => (
                  <tr key={form.ID}>
                    <td width="1%">
                      <i className="fas fa-list font-20" aria-hidden="true"></i>
                    </td>
                    <td>
                      <p>{form.form_name}</p>
                      {/* Pe ce tipuri iese ce se schimba aici - la formularele de
                          tichet asta se vede deja, si costa la fel de putin. */}
                      <span className="color-light-grey" style={{ fontSize: 11 }}>
                        {form.used_by.length > 0 ? (
                          <>
                            <i className="fas fa-link" aria-hidden="true"></i> used by {form.used_by.join(', ')}
                          </>
                        ) : (
                          <>
                            <i className="fas fa-unlink" aria-hidden="true"></i> not assigned
                          </>
                        )}
                      </span>
                    </td>
                    <td></td>
                    <td>
                      Fields: <span className="label label-success">{form.fields}</span>
                    </td>
                    <td>
                      Custom Fields: <span className="label label-warning">{form.custom_fields}</span>
                    </td>
                    <td width="100px">
                      <div className="btn-group pull-right" role="group">
                        <button
                          type="button"
                          className="btn btn-xs btn-primary edit-ticket-form"
                          onClick={() => setEdited(form.ID)}
                        >
                          <i className="fas fa-pencil-alt" aria-hidden="true"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-xs btn-danger remove-ticket-form"
                          onClick={() => {
                            if (!window.confirm('Are you sure?\nYou will not be able to recover this form!')) return
                            remove.mutate(form.ID)
                          }}
                        >
                          <i className="fas fa-times" aria-hidden="true"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {edited > 0 ? (
        <UpdateFormEditorPanel
          form={edited}
          onClose={() => setEdited(0)}
          onSaved={() => {
            setEdited(0)
            reload()
          }}
        />
      ) : null}

      <NewUpdateFormModal open={creating} onClose={() => setCreating(false)} onSaved={reload} />
    </div>
  )
}
